import { useEffect, useMemo } from 'react';
// ============================================================
// CharacterShelf - a folder tree of characters (or maps), for picking.
//
// Groups and the characters in them, read from a folder the GM picked: the
// character panel's Folder tab and Build's map folder import draw it. It
// began as the folder Workshop's tree (v8.14.0); the Workshop retired into
// the Assets page (assets stage 4) and took its editing slots with it, so
// what is left is selection and one slot of buttons beside each name.
// ============================================================
import type { ReactNode } from 'react';
import clsx from '../../utils/clsx';
import styles from '../../styles/Workshop.module.css';

/** A character's place in the tree - unique, and stable across renders. */
export function charPath(groupName: string | null, dirName: string): string {
  return groupName ? `${groupName}/${dirName}` : dirName;
}

/**
 * THE MINIMUM THIS DRAWS. A name, and iteration names under it.
 *
 * The shelf is generic over the entry because its two callers hold
 * genuinely different things: the Workshop has file-system HANDLES
 * (it writes), the session has `File` objects (it only reads). The
 * first version bridged that with
 *
 *     { name, handle: null, files } as unknown as CharacterDirEntry
 *
 * - a cast telling the compiler a null was a handle. Nothing
 * dereferenced it, so nothing broke; the day something did it would
 * have been a runtime crash the type system had been instructed not to
 * look for. Two implementations of a capability is a design choice. A
 * lie in a type is debt (v8.25.0).
 */
export interface ShelfEntry {
  name: string;
  files: { fileName: string }[];
}

export interface ShelfGroupEntry<C extends ShelfEntry = ShelfEntry> {
  name: string;
  characters: C[];
}

interface ShelfTree<C extends ShelfEntry = ShelfEntry, G extends ShelfGroupEntry<C> = ShelfGroupEntry<C>> {
  rootCharacters: C[];
  groups: G[];
}

export interface CharacterShelfProps<C extends ShelfEntry, G extends ShelfGroupEntry<C>> {
  tree: ShelfTree<C, G>;
  /** Group names currently rolled up. */
  collapsed: Set<string>;
  onToggleGroup: (name: string) => void;
  /** Buttons beside a character's name: a version picker. */
  characterActions?: (dir: C, groupName: string | null) => ReactNode;
  /** Buttons beside a folder's name. */
  groupActions?: (group: G) => ReactNode;
  /**
   * SELECTION MODE. Given a set, every character name becomes a
   * selectable row and `onToggleSelect` receives the event, so the
   * grammar in `utils/listSelection` means the same here as in the list.
   */
  selected?: Set<string>;
  onToggleSelect?: (path: string, e: React.MouseEvent, remove?: boolean) => void;
  /** Clicking a FOLDER's name takes all of it, or none. */
  onSelectGroup?: (paths: string[]) => void;
  /** The paths as drawn, top to bottom, so a shift-range walks what is on screen. */
  /**
   * The draw order, for shift-ranges.
   *
   * REPORTED FROM AN EFFECT, so it must land in a ref and NOT in state:
   * setting state here renders again, which fires the effect again, and
   * the loop presents as the app (or the test runner) simply hanging.
   * Memoise the `tree` you pass for the same reason.
   */
  onOrder?: (paths: string[]) => void;
}

type ShelfProps<C extends ShelfEntry, G extends ShelfGroupEntry<C>> = CharacterShelfProps<C, G>;

interface CharacterRowProps<C extends ShelfEntry, G extends ShelfGroupEntry<C>> {
  shelf: ShelfProps<C, G>;
  dir: C;
  groupName: string | null;
}

/** The character's name: a picking toggle in selection mode, plain text otherwise. */
function CharacterName<C extends ShelfEntry, G extends ShelfGroupEntry<C>>({
  shelf,
  dir,
  path,
}: {
  shelf: ShelfProps<C, G>;
  dir: C;
  path: string;
}) {
  const { selected, onToggleSelect } = shelf;
  if (!selected || !onToggleSelect) return <span className={styles.charName}>{dir.name}</span>;
  return (
    <button
      type="button"
      className={clsx(styles.charName, selected.has(path) && styles.charNamePicked)}
      aria-pressed={selected.has(path)}
      onClick={(e) => onToggleSelect(path, e)}
      onContextMenu={(e) => {
        // Right-click takes it OUT - and the browser's own menu
        // is never what somebody picking from a list wants.
        e.preventDefault();
        onToggleSelect(path, e, true);
      }}
    >
      {dir.name}
    </button>
  );
}

function ShelfCharacter<C extends ShelfEntry, G extends ShelfGroupEntry<C>>({
  shelf,
  dir,
  groupName,
}: CharacterRowProps<C, G>) {
  const path = charPath(groupName, dir.name);
  return (
    <div className={styles.charGroup} data-testid={`shelf-char-${path}`}>
      <div className={styles.charHeader}>
        <CharacterName shelf={shelf} dir={dir} path={path} />
        {shelf.characterActions && (
          <div className={styles.charHeaderActions}>{shelf.characterActions(dir, groupName)}</div>
        )}
      </div>
    </div>
  );
}

function ShelfGroup<C extends ShelfEntry, G extends ShelfGroupEntry<C>>({
  shelf,
  entry,
}: {
  shelf: ShelfProps<C, G>;
  entry: G;
}) {
  const { collapsed, onToggleGroup, selected, onToggleSelect, onSelectGroup, groupActions } = shelf;
  const rolled = collapsed.has(entry.name);
  const picking = !!selected && !!onToggleSelect;
  return (
    <div className={styles.folderGroup} data-testid={`shelf-group-${entry.name}`}>
      <div className={styles.folderHeader}>
        <button
          className={styles.folderToggle}
          aria-expanded={!rolled}
          aria-label={`${rolled ? 'Expand' : 'Collapse'} folder ${entry.name}`}
          onClick={() => onToggleGroup(entry.name)}
        >
          {rolled ? '▸' : '▾'} 📁 {entry.name}
        </button>
        {picking && onSelectGroup && (
          <button
            type="button"
            className={styles.folderPick}
            data-testid={`shelf-pick-group-${entry.name}`}
            title={`Select everyone in ${entry.name}`}
            onClick={() => onSelectGroup(entry.characters.map((c) => charPath(entry.name, c.name)))}
          >
            ⛶
          </button>
        )}
        {groupActions && <div className={styles.charHeaderActions}>{groupActions(entry)}</div>}
      </div>
      {!rolled && (
        <div className={styles.folderChildren}>
          {entry.characters.length === 0 && <div className={styles.emptyFiles}>(empty folder)</div>}
          {entry.characters.map((dir) => (
            <ShelfCharacter key={charPath(entry.name, dir.name)} shelf={shelf} dir={dir} groupName={entry.name} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CharacterShelf<C extends ShelfEntry, G extends ShelfGroupEntry<C>>(
  shelf: CharacterShelfProps<C, G>,
) {
  const { tree, onOrder } = shelf;
  return (
    <>
      {onOrder && <ShelfOrder tree={tree} onOrder={onOrder} />}
      {tree.groups.map((entry) => (
        <ShelfGroup key={entry.name} shelf={shelf} entry={entry} />
      ))}
      {tree.rootCharacters.map((dir) => (
        <ShelfCharacter key={charPath(null, dir.name)} shelf={shelf} dir={dir} groupName={null} />
      ))}
    </>
  );
}

/**
 * Report the draw order upward, once per tree.
 *
 * A shift-range has to walk the list AS DRAWN - groups first, then the
 * loose characters - and only the shelf knows that. Reporting it from an
 * effect keeps the parent from having to rebuild the same order out of
 * the same tree and get it subtly different.
 */
function ShelfOrder<C extends ShelfEntry, G extends ShelfGroupEntry<C>>({
  tree,
  onOrder,
}: {
  tree: ShelfTree<C, G>;
  onOrder: (paths: string[]) => void;
}) {
  const paths = useMemo(
    () => [
      ...tree.groups.flatMap((g) => g.characters.map((c) => charPath(g.name, c.name))),
      ...tree.rootCharacters.map((c) => charPath(null, c.name)),
    ],
    [tree],
  );
  useEffect(() => {
    onOrder(paths);
  }, [paths, onOrder]);
  return null;
}
